import { roleLabel, T, tt } from '@agos/shared'
import { cn, IconButton, StatusBadge } from '@agos/ui'
import { AlertTriangle, Copy, Pencil, Trash2 } from 'lucide-react'

import { initialsOf } from '../../employees/staff-roster'
import type { RolesOverview, RoleView } from '../../roles/roles-api'
import {
  edgesText,
  mayEditRights,
  membersOf,
  peopleCountText,
  rightsFigures,
  rightsFiguresText,
  roleWarnings,
} from '../role-model'
import { RoleChip } from '../RoleChip'

/**
 * One role on the *Role* tab - design §2.9's card.
 *
 * Its badge, *Vstavaná* or *Vlastná*, *Pracuje ako* on a custom role, up to five people's initials and a count that
 * opens *Ľudia* on them, *N práv · M z nich kontroluje server*, the warnings that are true, and the delegation in
 * words. *Hlavný administrátor* is read-only: it holds everything and is handed out only on *Zamestnanci*.
 */
export function RoleCard({
  role,
  overview,
  roles,
  mainAdmin,
  unsaved,
  onEdit,
  onPeople,
  onRename,
  onDuplicate,
  onDelete,
}: {
  role: RoleView
  overview: RolesOverview
  roles: readonly RoleView[]
  mainAdmin: boolean
  unsaved: boolean
  onEdit: () => void
  onPeople: () => void
  onRename: () => void
  onDuplicate: () => void
  onDelete: () => void
}) {
  // The count is the server's; the initials are the people the reader may see, which for a delegate is fewer.
  const members = membersOf(role.key, overview.people)
  const count = role.memberCount
  const figures = rightsFigures(role.permissions, overview.allPermissions, overview.serverChecked)
  const warnings = roleWarnings(role, overview.roleListReach)
  const editable = mayEditRights(role, mainAdmin)
  const manages = edgesText(role.manages, roles)
  const managedBy = edgesText(role.managedBy, roles)
  const isMainAdmin = role.key === 'main_admin'

  return (
    <div
      className="rounded-2xl border border-border bg-card p-4 space-y-3 flex flex-col"
      data-role-card={role.key}
      data-role-kind={role.builtIn ? 'built-in' : 'custom'}
    >
      <div className="flex flex-wrap items-center gap-2">
        <RoleChip roleKey={role.key} roles={roles} size="md" />
        <StatusBadge
          label={role.builtIn ? tt('Vstavaná') : tt('Vlastná')}
          color={role.builtIn ? 'zinc' : 'violet'}
        />
        {unsaved && (
          <span className="text-[11px] font-bold text-amber-500" data-role-unsaved>
            {tt('Neuložené zmeny')}
          </span>
        )}
      </div>

      {!role.builtIn && (
        <p className="text-xs text-muted-foreground" data-role-base={role.baseRole}>
          {tt('Pracuje ako')}: <strong className="text-foreground">{roleLabel(role.baseRole)}</strong>
        </p>
      )}
      {(role.description ?? '').trim() !== '' && (
        <p className="text-xs text-muted-foreground" data-role-description>
          {role.description}
        </p>
      )}

      {/* The people, as initials and a count - which open *Ľudia* on this role. */}
      <button
        type="button"
        onClick={onPeople}
        className="flex items-center gap-2 text-left text-xs hover:underline"
        data-role-members={count}
      >
        <span className="flex -space-x-1.5">
          {members.slice(0, 5).map((person) => (
            <span
              key={person.id}
              title={person.name}
              className="w-6 h-6 rounded-full border border-card bg-muted text-[10px] font-bold flex items-center justify-center"
            >
              {initialsOf(person.name)}
            </span>
          ))}
        </span>
        <span>{peopleCountText(count)}</span>
      </button>

      {isMainAdmin ? (
        <p className="text-xs text-muted-foreground" data-role-main-admin>
          <T>Má vždy všetko · prideľuje ho len hlavný administrátor v Zamestnancoch</T>
        </p>
      ) : (
        <p className="text-xs" data-role-rights={figures.rights} data-role-server-checked={figures.serverChecked}>
          {rightsFiguresText(figures)}
        </p>
      )}

      {warnings.map((warning) => (
        <p
          key={warning.kind}
          className="flex items-start gap-1.5 text-xs text-amber-500"
          data-role-warning={warning.kind}
        >
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
          <span>{warning.text}</span>
        </p>
      ))}

      {(manages !== '' || managedBy !== '') && (
        <div className="text-xs space-y-0.5">
          {manages !== '' && (
            <p data-role-manages>
              <span className="text-muted-foreground">{tt('Spravuje')}:</span> {manages}
            </p>
          )}
          {managedBy !== '' && (
            <p data-role-managed-by>
              <span className="text-muted-foreground">{tt('Spravuje ju')}:</span> {managedBy}
            </p>
          )}
        </div>
      )}

      {!isMainAdmin && (
        <div className="flex flex-wrap items-center gap-2 pt-1 mt-auto">
          <button
            type="button"
            onClick={onEdit}
            className={cn(
              'px-3 py-1.5 rounded-lg border text-xs font-semibold',
              editable ? 'border-primary/40 text-primary hover:bg-primary/10' : 'border-border hover:bg-muted/50',
            )}
            data-role-edit={role.key}
          >
            {editable ? tt('Upraviť práva') : tt('Zobraziť práva')}
          </button>
          {mainAdmin && (
            <div className="flex items-center gap-1.5 ml-auto">
              {!role.builtIn && (
                <IconButton label={tt('Premenovať')} onClick={onRename} attributes={{ 'data-role-rename': role.key }}>
                  <Pencil className="w-3.5 h-3.5" />
                </IconButton>
              )}
              <IconButton
                label={tt('Duplikovať')}
                onClick={onDuplicate}
                attributes={{ 'data-role-duplicate': role.key }}
              >
                <Copy className="w-3.5 h-3.5" />
              </IconButton>
              {!role.builtIn && (
                <IconButton
                  label={tt('Zmazať')}
                  tone="red"
                  onClick={onDelete}
                  attributes={{ 'data-role-delete': role.key }}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </IconButton>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
